import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react';
import UserProfile, { userProfileSchema, generateDefaultUserProfile } from '../models/UserProfile';
import { useAuth } from '../modules/authentication/AuthProvider';
import { database } from '../lib/supabase';
import i18next from 'i18next';

type UserProfileContextType = {
    userProfile: UserProfile | undefined;
    updateUserProfile: (updatedProfile: Partial<UserProfile>) => Promise<void>;
    isSaving: boolean;
    telegramRegistered: boolean;
    telegramToken: string | null;
    localProfile: UserProfile | undefined;
    setLocalProfile: (updatedProfile: Partial<UserProfile>) => void;
    handleLocalProfileChange: (field: string, value: any) => void;
    hasPendingChanges: boolean;
    updateTelegramRegistration: () => Promise<void>;
    subscriptionData: any | null;
    fetchSubscriptionData: () => Promise<void>;
};

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [userProfile, setUserProfile] = useState<UserProfile | undefined>(undefined);
    const [localProfile, setLocalProfile] = useState<UserProfile | undefined>(undefined);
    const [subscriptionData, setSubscriptionData] = useState<any | null>(null);
    const { currentUser } = useAuth();

    const [telegramRegistered, setTelegramRegistered] = useState<boolean>(false);

    useEffect(() => {
        if (!currentUser) {
            return;
        }

        setUserProfile(undefined);
        setLocalProfile(undefined);
        setSubscriptionData(null);

        const loadUserProfile = () => {
            database.userProfiles.table()
                .select('*')
                .eq('id', currentUser.id)
                .single()
                .then((value) => {
                    if (value.error) {
                        if (value.error.code === 'PGRST116') {
                            // No matching record found, use default profile
                            const defaultProfile = generateDefaultUserProfile(currentUser.id) as UserProfile;
                            setUserProfile(defaultProfile);
                            setLocalProfile(defaultProfile);

                            database.userProfiles.table().upsert(defaultProfile)
                                .then((value) => {
                                    if (value.error) {
                                        console.error('Error creating default profile:', value.error);
                                    } else {
                                        console.log('Default profile created');
                                    }
                                });
                        } else {
                            console.error(value.error);
                        }
                        return;
                    }

                    const data = value.data;
                    const userProfile = userProfileSchema.parse(data);
                    setUserProfile(userProfile);
                    setLocalProfile(userProfile);
                })
        }

        const userProfileSubscription = database.userProfiles.getChannel()
            .on('postgres_changes', { event: '*', schema: 'public', table: 'user_profiles' }, (payload) => {
                console.log({ payload });
                loadUserProfile();
            })
            .subscribe(() => {
                loadUserProfile();
            });

        const fetchSubscriptionData = async () => {
            if (!currentUser) return;

            const { data, error } = await database.stripeCustomerSubscriptions.table()
                .select('*')
                .eq('user_id', currentUser.id)
                .limit(1)
                .maybeSingle();

            if (error) {
                console.error('Error fetching subscription data:', error);
            } else {
                setSubscriptionData(data);
            }
        };

        fetchSubscriptionData();

        const subscriptionDataSubscription = database.stripeCustomerSubscriptions.getChannel()
            .on('postgres_changes', { event: '*', schema: 'public', table: 'stripe_customer_subscriptions' }, (payload) => {
                console.log('Subscription data changed:', payload);
                fetchSubscriptionData();
            })
            .subscribe();

        return () => { 
            userProfileSubscription.unsubscribe();
            subscriptionDataSubscription.unsubscribe();
        }
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser) {
            return;
        }

        let isMounted = true;

        const checkTelegramRegistry = async () => {
            if (!isMounted) return;

            const { data, error } = await database.telegramChats.table()
                .select('*')
                .eq('user_id', currentUser.id)
                .limit(1)
                .maybeSingle();

            console.log({ data, error });

            if (error) {
                console.error('Error checking Telegram chat entry:', error);
                if (isMounted) setTelegramRegistered(false);
            } else {
                if (isMounted) setTelegramRegistered(!!data);
            }
        };

        checkTelegramRegistry();

        const subscription = database.telegramChats.getChannel()
            .on('postgres_changes', { event: '*', schema: 'public', table: 'user_telegram_chats' }, () => {
                checkTelegramRegistry();
            })
            .subscribe(() => {
                checkTelegramRegistry();
            });

        return () => { 
            isMounted = false;
            subscription.unsubscribe(); 
        }
    }, [currentUser]);

    const [isSaving, setIsSaving] = useState(false);
    const [hasPendingChanges, setHasPendingChanges] = useState(false);

    const updateUserProfile = async (updatedProfile: Partial<UserProfile>) => {
        if (!currentUser || !userProfile) return;

        setIsSaving(true);

        const newProfile = { 
            ...userProfile, 
            ...updatedProfile, 
            id: currentUser.id, 
            language: i18next.language
        };
        
        const { error } = await database.userProfiles.table().upsert(newProfile);

        if (error) {
            console.error('Error updating user profile:', error);
        } else {
            setUserProfile(newProfile);
            setLocalProfile(newProfile);
        }
        setIsSaving(false);
        setHasPendingChanges(false);
    };

    const [telegramToken, setTelegramToken] = useState<string | null>(null);
    useEffect(() => {
        const generateAndSaveToken = async () => {
            if (!currentUser) return;

            const token = Math.floor(10000000 + Math.random() * 90000000).toString();
            console.log('Generating telegram token:', token);
            const { error } = await database.telegramTokens.table()
                .upsert({
                    id: currentUser.id,
                    user_id: currentUser.id,
                    token: token,
                    expires_at: new Date(Date.now() + 1000 * 60 * 60 * 24),
                    used: false,
                });

            if (error) {
                console.error('Error saving telegram token:', error);
                return;
            } else {
                console.log('Telegram token saved:', token);
            }

            setTelegramToken(token);
        };

        generateAndSaveToken();
    }, [currentUser]);

    const changeLocalProfile = (updatedProfile: Partial<UserProfile>) => {
        setLocalProfile((prev) => {
            if (prev === undefined) {
                return updatedProfile as UserProfile;
            }
            return { ...prev, ...updatedProfile };
        });
        setHasPendingChanges(true);
    };

    const handleLocalProfileChange = (field: string, value: any) => {
        changeLocalProfile({ [field]: value });
    };

    const updateTelegramRegistration = async () => {
        if (!currentUser) return;

        const { data, error } = await database.telegramChats.table()
            .select('*')
            .eq('user_id', currentUser.id)
            .limit(1)
            .maybeSingle();

        if (error) {
            console.error('Error checking Telegram chat entry:', error);
            setTelegramRegistered(false);
        } else {
            setTelegramRegistered(!!data);
        }
    };

    const fetchSubscriptionData = async () => {
        if (!currentUser) return;

        const { data, error } = await database.stripeCustomerSubscriptions.table()
            .select('*')
            .eq('user_id', currentUser.id)
            .single();

        if (error) {
            console.error('Error fetching subscription data:', error);
        } else {
            setSubscriptionData(data);
        }
    };

    return (
        <UserProfileContext.Provider value={{ 
            userProfile, 
            updateUserProfile, 
            isSaving, 
            telegramRegistered, 
            telegramToken, 
            localProfile, 
            setLocalProfile: changeLocalProfile, 
            handleLocalProfileChange, 
            hasPendingChanges, 
            updateTelegramRegistration,
            subscriptionData: subscriptionData ? { 
                ...subscriptionData, 
                isSubscribed: 
                    subscriptionData?.subscription_status === 'active' || 
                    subscriptionData?.subscription_status === 'cancelling'
                } : null,
            fetchSubscriptionData
        }}>
            {children}
        </UserProfileContext.Provider>
    )
}

export function useUserProfile(): UserProfileContextType {
    const context = useContext(UserProfileContext);
    if (context === undefined) {
        throw new Error('useUserProfile must be used within a UserProfileProvider');
    }
    return context;
}

